import { useEffect, useState } from "react";
import ModalWrapper from "../../ModalWrapper";
import BDcoin from "../../BDcoin";
import { useWalletDetails } from "../../../hooks/api/useWallet";
import { formatNumber } from "../../../helper";
import { purchaseNaray8Chapters } from "../../../redux/naray8/naray8Action";
import { useQueryClient } from "@tanstack/react-query";
import InfoModal from "../../ModalComponents/InfoModal";
import SuccessModal from "../../ModalComponents/SuccessModal";
import ErrorModal from "../../ModalComponents/ErrorModal";
import CoinModal from "./CoinModal";

const BoxSVG = () => (
    <svg width="31" height="28" viewBox="0 0 31 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <mask id="mask0_158_12377" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <path fillRule="evenodd" clipRule="evenodd" d="M1.80469 7.05566H22.8047L21.638 22.8057H2.97135L1.80469 7.05566Z" fill="white" stroke="white" strokeWidth="2" strokeLinejoin="round" />
            <path d="M7.63867 9.389V1.80566H16.972V9.389" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.63867 18.1389H16.972" stroke="black" strokeWidth="2" strokeLinecap="round" />
        </mask>
        <g mask="url(#mask0_158_12377)">
            <path d="M-1.69531 -1.69434H26.3047V26.3057H-1.69531V-1.69434Z" fill="white" />
        </g>
        <path d="M24.1377 14.6553C24.926 14.6553 25.5652 15.2947 25.5654 16.083V18.9336H28.416C29.2044 18.9337 29.8438 19.5729 29.8438 20.3613V21.1387C29.8438 21.9271 29.2044 22.5663 28.416 22.5664H25.5654V25.417C25.5653 26.2054 24.9261 26.8447 24.1377 26.8447H23.3604C22.572 26.8447 21.9327 26.2054 21.9326 25.417V22.5664H19.082C18.2938 22.5662 17.6543 21.927 17.6543 21.1387V20.3613C17.6543 19.573 18.2938 18.9338 19.082 18.9336H21.9326V16.083C21.9329 15.2947 22.572 14.6553 23.3604 14.6553H24.1377Z" fill="white" stroke="#353530" strokeWidth="1.3" />
    </svg>
)

export default function ChapterPurchaseFlow({ narray8Id, chapters, open, setOpen }) {
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState([]);
    const [step, setStep] = useState("select"); // select | confirm | insufficient | coin-purchase | success | error
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const { data } = useWalletDetails()
    const bdTotalSpendableBalance = data?.totalSpendableCoins || 0

    const openTopUp = () => {
        setStep("coin-purchase");
    };

    const toggleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const totalCost = selected.reduce((sum, id) => {
        const chap = chapters.find((c) => c.chapterId === id);
        const price = Number(chap?.price) || 0;
        return sum + (price || 0);
    }, 0);

    const handlePurchase = async () => {
        if (totalCost > bdTotalSpendableBalance) {
            setStep("insufficient");
            return;
        }

        try {
            setLoading(true);
            await purchaseNaray8Chapters({
                narrateId: narray8Id,
                chapterIds: selected
            })
            queryClient.invalidateQueries({ queryKey: ["wallet", "details"] })
            queryClient.invalidateQueries({ queryKey: ["naray8", narray8Id] })
            setStep("success");
        } catch (err) {
            setError(err?.message || "An error occurred while processing your purchase.");
            setStep("error");
        } finally {
            setLoading(false);
        }
    };

    // FIX BUG #2: Defensive initialization to prevent double-counting
    useEffect(() => {
        if (!open) {
            setStep("select");
            setSelected([]);
        } else {
            // Handle case where open might be an array or have different structure
            let initialSelection = [];

            if (open?.chapterId) {
                if (Array.isArray(open.chapterId)) {
                    // If it's an array, take only the first item (defensive)
                    initialSelection = open.chapterId.slice(0, 1);
                } else {
                    initialSelection = [open.chapterId];
                }
            } else if (Array.isArray(open)) {
                // If open itself is an array
                initialSelection = open.slice(0, 1);
            } else if (typeof open === 'string') {
                // If open is just a string ID
                initialSelection = [open];
            }

            // Remove duplicates and ensure we have unique chapter IDs
            setSelected([...new Set(initialSelection)]);
        }
    }, [open])

    if (!open) return null;

    return (
        <>
            <CoinModal open={step === "coin-purchase"} onClose={() => setStep("select")} />

            {step === "select" && (
                <ModalWrapper
                    className="max-w-[min(75vw,1039px)]"
                    onClose={() => setOpen(null)}
                    disableHeaderBorder
                    title="Select the number of chapters you want to unlock"
                    subTitleSection={
                        <div className="w-full flex items-center justify-between mt-1">
                            <div className="flex gap-1 items-center font-sora text-sm">
                                Coin Balance:
                                <BDcoin /> <span className="text-[9px]">{formatNumber(bdTotalSpendableBalance)}</span>
                            </div>

                            <button onClick={() => setStep("coin-purchase")}>
                                <BoxSVG />
                            </button>
                        </div>
                    }
                >
                    <div className="flex flex-col p-4 pt-0">
                        <div className="self-end flex gap-1 items-center justify-center font-sora w-[50px] h-[25px] text-[9px] bg-white/15 border-[#9B9B9B] border rounded-[2px] mb-3">
                            <BDcoin />
                            {totalCost}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 max-h-[300px] overflow-y-auto scrollbar-hide">
                            {chapters.map((chap) => {
                                const isSelected = selected.includes(chap.chapterId);
                                return (
                                    <button
                                        key={chap.chapterId}
                                        onClick={() => toggleSelect(chap.chapterId)}
                                        className={`flex gap-2 justify-between items-center px-3 py-1 h-[30px] rounded-[5px] text-left transition-all ${isSelected
                                            ? "bg-customGold text-black"
                                            : "bg-white/15 shadow-sm shadow-black/20 text-white hover:opacity-80"
                                            }`}
                                    >
                                        <div title={chap.title} className="max-w-[60%] line-clamp-1 text-xs font-sora font-semibold">{chap.title}</div>
                                        <div className="text-xs font-sora font-semibold mt-1 flex flex-row items-center gap-0.5">
                                            <BDcoin /> {chap.price || 0}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                disabled={!selected.length}
                                onClick={() => setStep(totalCost > bdTotalSpendableBalance ? "insufficient" : "confirm")}
                                className="w-full max-w-[350px] bg-[#BE9524] hover:opacity-90 disabled:bg-[#826B2D] shadow-lg shadow-black/50 py-3 rounded-[5px] text-lg font-sora font-semibold"
                            >
                                Unlock {selected.length} chapter{selected.length !== 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>
                </ModalWrapper>
            )}

            {step === "confirm" && (
                <InfoModal
                    title="Confirm Purchase"
                    info={`You are about to unlock ${selected.length} chapter${selected.length !== 1 ? 's' : ''} for ${totalCost} coins.`}
                    onContinue={handlePurchase}
                    open={true}
                    loading={loading}
                    setOpen={() => setStep("select")}
                />
            )}

            {step === "insufficient" && (
                <InfoModal
                    title="Insufficient Balance"
                    info={`You need ${formatNumber(totalCost)} coins to unlock ${selected.length} chapter${selected.length !== 1 ? 's' : ''}, but you only have ${formatNumber(bdTotalSpendableBalance)}.`}
                    open={true}
                    setOpen={() => setStep("select")}
                    cancelText="Back"
                    extraButton={{
                        text: "Buy Coins",
                        onClick: openTopUp,
                    }}
                />
            )}

            {step === "success" && (
                <SuccessModal
                    message={"Chapters unlocked successfully!"}
                    onClose={() => setOpen(false)}
                />
            )}

            {step === "error" && (
                <ErrorModal
                    message={error || "An error occurred while processing your purchase."}
                    onClose={() => setStep("select")}
                    onRetry={() => setStep("confirm")}
                />
            )}

        </>
    );
}